import { useCallback } from 'react';
import { Platform, Image } from 'react-native';
import * as ImagePicker from '../utils/image-picker/image.picker';
import { FileTypeEnum, mediaInfoFrom } from '@matap/shared';
import * as DocumentPicker from 'expo-document-picker';
import { useImageCompressor } from './use.image.compressor';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionShowAlert } from '@/redux';
import { FileAsset, Uploadable } from '../file-manager/file.asset';
import { MEDIA_CONFIGS } from '@/constants';
import { numbersToEnglish } from '../utils';

export function useFilePicker() {
  const dispatch = useAppDispatch();
  const compressor = useImageCompressor();

  const pickGallery = useCallback(async () => {
    return ImagePicker.launchImageLibraryAsync({
      mediaTypes: 'images',
      allowsEditing: Platform.OS !== 'web',
      allowsMultipleSelection: false,
      exif: false,
      legacy: Platform.OS === 'android' && Platform.Version < 33,
      defaultTab: 'photos',
    }).then(async (res) => {
      if (res.canceled) {
        return;
      }

      const asset = await compressor.compress(res.assets[0]);
      return FileAsset.create({
        localAddress: asset.uri,
        type: FileTypeEnum.IMAGE,
        mediaMetaData: { width: asset.width, height: asset.height },
        fileName: asset.fileName,
        mime: asset.mimeType || res.assets[0].mimeType,
      } as Uploadable);
    });
  }, [compressor]);

  const pickCamera = useCallback(async () => {
    return ImagePicker.launchCameraAsync({
      mediaTypes: 'images',
      allowsEditing: Platform.OS !== 'web',
      allowsMultipleSelection: false,
      exif: false,
    }).then(async (res) => {
      if (res.canceled) {
        return;
      }

      const asset = await compressor.compress(res.assets[0]);
      return FileAsset.create({
        localAddress: asset.uri,
        type: FileTypeEnum.IMAGE,
        mediaMetaData: { width: asset.width, height: asset.height },
        fileName: asset.fileName,
        mime: asset.mimeType || res.assets[0].mimeType,
      } as Uploadable);
    });
  }, [compressor]);

  const pickPdf = useCallback(async () => {
    return DocumentPicker.getDocumentAsync({
      type: 'application/pdf',
      multiple: false,
      copyToCacheDirectory: true,
    }).then(async (res) => {
      if (res.canceled) {
        return;
      }

      const info = await mediaInfoFrom(res.assets[0].uri);

      if (info.mime !== 'application/pdf') {
        dispatch(actionShowAlert(`فرمت فایل انتخاب شده پی دی اف نمی باشد`));
        return;
      }
      const asset = res.assets[0];
      return FileAsset.create({
        localAddress: asset.uri,
        type: FileTypeEnum.PDF,
        size: asset.size,
        fileName: asset.name,
        mime: asset.mimeType || res.assets[0].mimeType,
        mediaMetaData: {},
      } as Uploadable);
    });
  }, [dispatch]);

  const pickVideo = useCallback(async () => {
    return ImagePicker.launchImageLibraryAsync({
      mediaTypes: 'videos',
      allowsEditing: Platform.OS !== 'web',
      allowsMultipleSelection: false,
      legacy: Platform.OS === 'android' && Platform.Version < 33,
      exif: false,
    }).then(async (res) => {
      if (res.canceled) {
        return;
      }

      const asset = res.assets[0];

      //50mb
      if (asset.fileSize != null && asset.fileSize > MEDIA_CONFIGS.maxVideoSizeMb * 1024 * 1024) {
        dispatch(actionShowAlert(numbersToEnglish(`حجم فایل انتخابی نمیتواند بیشتر از ${MEDIA_CONFIGS.maxVideoSizeMb} مگابایت باشد`)));
        return;
      }

      return FileAsset.create({
        localAddress: asset.uri,
        type: FileTypeEnum.VIDEO,
        mediaMetaData: { width: asset.width, height: asset.height, duration: asset.duration },
        fileName: asset.fileName,
        size: asset.fileSize,
        mime: asset.mimeType || res.assets[0].mimeType,
      } as Uploadable);
    });
  }, [dispatch]);

  const pickFile = useCallback(async () => {
    return DocumentPicker.getDocumentAsync({
      type: '*/*',
      multiple: false,
      copyToCacheDirectory: true,
    }).then(async (res) => {
      if (res.canceled) {
        return;
      }

      const asset = res.assets[0];

      // Determine the precise MIME type and name using your utility
      const info = await mediaInfoFrom(asset.uri);
      const mime = info.mime.toLowerCase().trim();

      // 1. Handle Images
      if (mime.startsWith('image/') && mime !== 'image/gif') {
        // We cast asset to 'any' here as DocumentPickerAsset might have slight type differences from ImagePickerAsset,
        // but your compressor should be able to handle the URI.
        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
          Image.getSize(
            asset.uri,
            (width, height) => resolve({ width, height }),
            () => resolve({ width: 0, height: 0 }), // Fallback on error
          );
        });
        (asset as any).width = dimensions.width;
        (asset as any).height = dimensions.height;
        (asset as any).fileName = asset.name;
        (asset as any).fileSize = asset.size;
        const compressedAsset = await compressor.compress(asset as any);
        console.log({ compressedAsset });
        return FileAsset.create({
          localAddress: compressedAsset.uri,
          type: FileTypeEnum.IMAGE,
          mediaMetaData: { width: compressedAsset.width, height: compressedAsset.height },
          fileName: compressedAsset.fileName || info.name,
          mime: compressedAsset.mimeType || mime,
        } as Uploadable);
      }

      // 2. Handle Videos
      if (mime.startsWith('video/')) {
        if (asset.size != null && asset.size > MEDIA_CONFIGS.maxVideoSizeMb * 1024 * 1024) {
          dispatch(actionShowAlert(numbersToEnglish(`حجم فایل انتخابی نمیتواند بیشتر از ${MEDIA_CONFIGS.maxVideoSizeMb} مگابایت باشد`)));
          return;
        }

        const metadata = await extractVideoMetadata(asset.uri);

        return FileAsset.create({
          localAddress: asset.uri,
          type: FileTypeEnum.VIDEO,
          mediaMetaData: {
            width: metadata.width,
            height: metadata.height,
            duration: metadata.duration,
          },
          fileName: info.name,
          size: asset.size,
          mime: mime,
        } as Uploadable);
      }

      // 3. Handle PDFs
      if (mime === 'application/pdf') {
        return FileAsset.create({
          localAddress: asset.uri,
          type: FileTypeEnum.PDF,
          size: asset.size,
          fileName: info.name,
          mime: mime,
          mediaMetaData: {},
        } as Uploadable);
      }

      // 4. Fallback for unsupported types
      dispatch(actionShowAlert(`فرمت فایل انتخاب شده پشتیبانی نمی‌شود`));
      return;
    });
  }, [compressor, dispatch]);

  return {
    pickGallery,
    pickCamera,
    pickPdf,
    pickVideo,
    pickFile,
  };
}

const extractVideoMetadata = async (uri: string): Promise<{ width?: number; height?: number; duration?: number }> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration * 1000,
      });
    };
    video.onerror = () => resolve({ width: undefined, height: undefined, duration: undefined });
    video.src = uri;
  });
};
